import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { MaterialPicker } from '../../components/pickers/MaterialPicker';
import { api, apiGet, apiPost } from '../../lib/api';
import Pagination from '../../components/Pagination';
import {
  MPR_PLAN_FREQUENCIES,
  MPR_SOURCE_BASES,
  mprStatusClass,
  blankLine,
} from './stores-constants';

// ========== MPR TAB (Material Requisition) ==========
export function MPRTab() {
  const [mprs, setMprs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [formMeta, setFormMeta] = useState({ plan_frequency: '', source_basis: '', required_by: '', justification: '' });
  const [lineItems, setLineItems] = useState([blankLine()]);

  // Filters + sort + toggle
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'completed'
  const [filterSearch, setFilterSearch] = useState('');
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [sortKey, setSortKey] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 20;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet('/mpr');
      setMprs(data?.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setFormMeta({ plan_frequency: '', source_basis: '', required_by: '', justification: '' });
    setLineItems([blankLine()]);
    setShowCreate(true);
  };
  const updateLine = (idx, field, val) => setLineItems(ls => ls.map((l, i) => i === idx ? { ...l, [field]: val } : l));
  const pickLineMaterial = (idx, mat) => setLineItems(ls => ls.map((l, i) => i === idx ? { ...l, material_id: mat?.id || '', material_name: mat?.name || '', uom: mat?.uom || '' } : l));
  const addLine = () => setLineItems(ls => [...ls, blankLine()]);
  const removeLine = (idx) => setLineItems(ls => ls.filter((_, i) => i !== idx));

  const submit = async (asDraft) => {
    const validLines = lineItems.filter(l => l.material_id && Number(l.qty) > 0);
    if (!validLines.length) { toast.error('Add at least one item with qty > 0'); return; }
    if (!formMeta.plan_frequency) { toast.error('Select a plan frequency'); return; }
    if (!formMeta.source_basis) { toast.error('Select a source basis'); return; }
    setSubmitting(true);
    try {
      await apiPost('/mpr', {
        ...formMeta,
        line_items: validLines.map(l => ({ material_id: l.material_id, qty: Number(l.qty), uom: l.uom, notes: l.notes })),
        submit_for_approval: !asDraft,
      });
      toast.success(asDraft ? 'MPR saved as Draft' : 'MPR submitted to PM for approval');
      setShowCreate(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save MPR'); }
    finally { setSubmitting(false); }
  };

  const mpr = detailId ? mprs.find(m => m.id === detailId) : null;

  // ── Completed = Closed / Fully Received; Active = everything else ──
  const COMPLETED_STATUSES = new Set(['closed', 'fully received', 'Closed', 'Fully Received']);
  const tabFiltered = mprs.filter(m =>
    activeTab === 'completed'
      ? COMPLETED_STATUSES.has(m.status || '')
      : !COMPLETED_STATUSES.has(m.status || '')
  );

  // ── Unique project / status options from current data ──
  const projectOptions = [...new Set(tabFiltered.map(m => m.project_name || m.project || '').filter(Boolean))].sort();
  const statusOptions  = [...new Set(tabFiltered.map(m => m.status || '').filter(Boolean))].sort();

  // ── Client-side filter ──
  const q = filterSearch.trim().toLowerCase();
  const filtered = tabFiltered.filter(m => {
    if (filterProject && (m.project_name || m.project || '') !== filterProject) return false;
    if (filterStatus  && (m.status || '') !== filterStatus) return false;
    if (q && !`${m.mpr_no} ${m.project_name || m.project || ''} ${(m.line_items || []).map(l => l.material_name || '').join(' ')}`.toLowerCase().includes(q)) return false;
    return true;
  });

  // ── Sort ──
  const sorted = [...filtered].sort((a, b) => {
    let va = a[sortKey] || '', vb = b[sortKey] || '';
    if (sortKey === 'created_at') { va = va || ''; vb = vb || ''; }
    const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const paginated = sorted.slice(start, start + PAGE_SIZE);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
    setPage(1);
  };
  const SortIcon = ({ k }) => sortKey !== k ? <span className="text-slate-400 ml-0.5 text-[10px]">↕</span> : <span className="text-[#1F7A6E] ml-0.5 text-[10px] font-bold">{sortDir === 'asc' ? '↑' : '↓'}</span>;

  return (
    <div className="space-y-4" data-testid="stores-mpr-tab">
      {mpr && <MPRDetail mpr={mpr} onClose={() => setDetailId(null)} onRefresh={() => { setDetailId(null); load(); }} />}
      <div className="flex justify-between items-center">
        <div className="text-sm text-gray-500">{sorted.length} of {mprs.length} Material Requisitions</div>
        <div className="flex items-center gap-2">
          <Button data-testid="mpr-new-btn" onClick={openCreate}><Plus className="w-4 h-4 mr-2" /> New MPR</Button>
        </div>
      </div>

      {/* ── Active / Completed toggle ── */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1 w-fit">
        {[['active', 'Active'], ['completed', 'Completed']].map(([val, label]) => (
          <button key={val} onClick={() => { setActiveTab(val); setFilterStatus(''); setFilterProject(''); setFilterSearch(''); setPage(1); }}
            className={`px-4 py-1.5 text-xs font-semibold rounded-md transition-colors ${activeTab === val ? 'bg-white text-[#1F7A6E] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`mpr-tab-${val}`}>
            {label} ({mprs.filter(m => val === 'completed' ? COMPLETED_STATUSES.has(m.status || '') : !COMPLETED_STATUSES.has(m.status || '')).length})
          </button>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-2 items-center">
        <input type="search" placeholder="Search MPR no, project, material…"
          value={filterSearch} onChange={e => setFilterSearch(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#1F7A6E] w-64"
          data-testid="mpr-filter-search" />
        <select value={filterProject} onChange={e => setFilterProject(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#1F7A6E] bg-white"
          data-testid="mpr-filter-project">
          <option value="">All Projects</option>
          {projectOptions.map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-slate-300 rounded-sm px-3 py-1.5 text-sm focus:outline-none focus:border-[#1F7A6E] bg-white"
          data-testid="mpr-filter-status">
          <option value="">All Statuses</option>
          {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        {(filterSearch || filterProject || filterStatus) && (
          <button onClick={() => { setFilterSearch(''); setFilterProject(''); setFilterStatus(''); setPage(1); }}
            className="text-xs text-slate-500 hover:text-rose-600 underline">Clear filters</button>
        )}
      </div>

      <Card><CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('mpr_no')}>
                  MPR No <SortIcon k="mpr_no" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('created_at')}>
                  Date Created <SortIcon k="created_at" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('project_name')}>
                  Project <SortIcon k="project_name" />
                </th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Items</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Plan Freq.</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600 cursor-pointer select-none hover:text-[#1F7A6E]" onClick={() => toggleSort('status')}>
                  Status <SortIcon k="status" />
                </th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">Loading...</td></tr>
              ) : sorted.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-8 text-gray-500">
                  {(filterSearch || filterProject || filterStatus) ? 'No results match the current filters.' : activeTab === 'completed' ? 'No completed MPRs yet.' : 'No active MPRs yet.'}
                </td></tr>
              ) : paginated.map(m => {
                const lc = (m.line_items || []).length || 1;
                const createdAt = m.created_at ? new Date(m.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                return (
                  <tr key={m.id} className="hover:bg-gray-50 cursor-pointer" data-testid={`mpr-row-${m.id}`} onClick={() => setDetailId(m.id)}>
                    <td className="px-4 py-3 font-mono text-xs">{m.mpr_no}</td>
                    <td className="px-4 py-3 text-xs whitespace-nowrap">{createdAt}</td>
                    <td className="px-4 py-3 text-xs max-w-[180px] truncate" title={m.project_name || m.project || '—'}>{m.project_name || m.project || '—'}</td>
                    <td className="px-4 py-3 text-xs">{lc > 1 ? <span className="font-medium text-[#1F7A6E]">{lc} items</span> : (m.item || m.material_name || '—')}</td>
                    <td className="px-4 py-3 text-xs">{m.plan_frequency || '—'}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${mprStatusClass(m.status)}`} data-testid={`mpr-status-${m.id}`}>{m.status}</span></td>
                    <td className="px-4 py-3 text-right"><button className="text-xs text-[#1F7A6E] hover:underline" onClick={e => { e.stopPropagation(); setDetailId(m.id); }}>View →</button></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalFiltered > PAGE_SIZE && (
          <div className="border-t border-slate-200 px-4 py-3">
            <Pagination
              page={safePage} pageSize={PAGE_SIZE} total={totalFiltered}
              start={start} end={Math.min(start + PAGE_SIZE, totalFiltered)}
              onPageChange={setPage} onPageSizeChange={() => {}}
              pageSizeOptions={[]} label="MPRs"
              data-testid="mpr-pagination"
            />
          </div>
        )}
      </CardContent></Card>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg w-full max-w-2xl my-8" data-testid="mpr-create-modal">
            <div className="p-5 border-b flex justify-between items-center">
              <div>
                <h2 className="text-base font-bold">New Material Requisition (MPR)</h2>
                <p className="text-xs text-gray-500 mt-0.5">Add multiple materials · PM approves → Procurement raises RFQ.</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}><XCircle className="w-4 h-4" /></Button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3 border-b">
              <div>
                <label className="block text-xs font-medium mb-1">Plan Frequency *</label>
                <select data-testid="mpr-form-plan-frequency" value={formMeta.plan_frequency} onChange={e => setFormMeta(f => ({ ...f, plan_frequency: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {MPR_PLAN_FREQUENCIES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Source Basis *</label>
                <select data-testid="mpr-form-source-basis" value={formMeta.source_basis} onChange={e => setFormMeta(f => ({ ...f, source_basis: e.target.value }))} className="w-full border rounded px-3 py-2 text-sm">
                  <option value="">Select…</option>
                  {MPR_SOURCE_BASES.map(f => <option key={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Required By</label>
                <Input type="date" data-testid="mpr-form-required-by" value={formMeta.required_by} onChange={e => setFormMeta(f => ({ ...f, required_by: e.target.value }))} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-medium mb-1">Justification</label>
                <textarea value={formMeta.justification} onChange={e => setFormMeta(f => ({ ...f, justification: e.target.value }))} rows={2} className="w-full border rounded px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-widest text-gray-600">Line Items ({lineItems.length})</span>
                <button onClick={addLine} className="text-xs text-[#1F7A6E] hover:underline flex items-center gap-1" data-testid="mpr-add-line-btn"><Plus size={11} /> Add Item</button>
              </div>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                {lineItems.map((li, idx) => (
                  <div key={li._key} className="grid grid-cols-12 gap-2 items-start bg-gray-50 rounded p-2" data-testid={`mpr-line-${idx}`}>
                    <div className="col-span-5">
                      <MaterialPicker value={li.material_id} displayName={li.material_name} onSelect={mat => pickLineMaterial(idx, mat)} testid={`mpr-line-material-${idx}`} />
                    </div>
                    <div className="col-span-2"><Input type="number" min="0" placeholder="Qty" value={li.qty} onChange={e => updateLine(idx, 'qty', e.target.value)} data-testid={`mpr-line-qty-${idx}`} /></div>
                    <div className="col-span-2"><Input placeholder="UoM" value={li.uom} onChange={e => updateLine(idx, 'uom', e.target.value)} /></div>
                    <div className="col-span-2"><Input placeholder="Notes" value={li.notes} onChange={e => updateLine(idx, 'notes', e.target.value)} /></div>
                    <div className="col-span-1 pt-1"><button onClick={() => removeLine(idx)} disabled={lineItems.length === 1} className="text-rose-500 disabled:opacity-30"><XCircle size={14} /></button></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 border-t bg-gray-50 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button variant="outline" size="sm" data-testid="mpr-draft-btn" disabled={submitting} onClick={() => submit(true)}>Save as Draft</Button>
              <Button size="sm" data-testid="mpr-save-btn" disabled={submitting} onClick={() => submit(false)}>
                {submitting && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Submit to PM
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== MPR DETAIL MODAL ==========
export function MPRDetail({ mpr, onClose, onRefresh }) {
  const [fulfillment, setFulfillment] = useState(null);
  const [showRfqDrawer, setShowRfqDrawer] = useState(false);
  const [vendorIds, setVendorIds] = useState('');
  const [splitMode, setSplitMode] = useState('single');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (mpr?.id) {
      apiGet(`/mpr/${mpr.id}/fulfillment`).then(r => setFulfillment(r)).catch(() => {});
    }
  }, [mpr?.id]);

  const createRfq = async () => {
    setCreating(true);
    try {
      const vids = vendorIds.split(',').map(v => v.trim()).filter(Boolean);
      const res = await api.post(`/mpr/${mpr.id}/create-rfq-for-items`, {
        items: [],
        vendor_ids: vids,
        split: splitMode,
      });
      toast.success(`${res.data.count} RFQ(s) created`);
      setShowRfqDrawer(false);
      onRefresh && onRefresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to create RFQ'); }
    finally { setCreating(false); }
  };

  const lineItems = fulfillment?.line_items || mpr?.line_items || [];
  const isApproved = (mpr?.status || '').toLowerCase().includes('approved');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-3xl my-8" data-testid="mpr-detail-modal">
        <div className="p-5 border-b flex justify-between items-center">
          <div>
            <h2 className="text-base font-bold">{mpr.mpr_no}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{mpr.project_name || mpr.project || ''} · {mpr.plan_frequency} · {mpr.source_basis}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${mprStatusClass(mpr.status)}`}>{mpr.status}</span>
            {isApproved && (
              <Button size="sm" variant="outline" onClick={() => setShowRfqDrawer(true)} data-testid="mpr-create-rfq-btn">
                Create RFQ
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
          </div>
        </div>
        <div className="p-5">
          <table className="w-full text-sm" data-testid="mpr-detail-items-table">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-3 py-2 font-medium text-gray-600">Material</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Required</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Received</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">Balance</th>
                <th className="text-right px-3 py-2 font-medium text-gray-600">%</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {lineItems.map((li, idx) => (
                <tr key={idx} data-testid={`mpr-detail-row-${idx}`}>
                  <td className="px-3 py-2">{li.material_name || li.material_id || '—'}</td>
                  <td className="px-3 py-2 text-right">{li.required_qty ?? li.qty ?? '—'} {li.uom}</td>
                  <td className="px-3 py-2 text-right">{li.received_qty ?? 0} {li.uom}</td>
                  <td className="px-3 py-2 text-right">{((li.required_qty ?? li.qty ?? 0) - (li.received_qty ?? 0))} {li.uom}</td>
                  <td className="px-3 py-2 text-right">{li.fulfillment_pct != null ? `${li.fulfillment_pct.toFixed(1)}%` : '0%'}</td>
                </tr>
              ))}
              {lineItems.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-4 text-center text-gray-500 text-xs">No line items</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {showRfqDrawer && (
          <div className="border-t p-5 bg-gray-50">
            <h3 className="text-sm font-bold mb-3">Create RFQ from MPR</h3>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium mb-1">Split Mode</label>
                <select value={splitMode} onChange={e => setSplitMode(e.target.value)} className="w-full border rounded px-3 py-2 text-sm" data-testid="mpr-rfq-split-mode">
                  <option value="single">Single RFQ (all vendors)</option>
                  <option value="per-vendor">Per Vendor (one RFQ each)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1">Vendor IDs (comma-separated)</label>
                <Input value={vendorIds} onChange={e => setVendorIds(e.target.value)} placeholder="vendor-id-1, vendor-id-2" data-testid="mpr-rfq-vendor-ids" />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setShowRfqDrawer(false)}>Cancel</Button>
              <Button size="sm" disabled={creating} onClick={createRfq} data-testid="mpr-rfq-create-btn">
                {creating && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Create RFQ(s)
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
